"use client";

import Link from "next/link";
import { MapPin, Calendar } from "lucide-react";
import { MdEmail } from "react-icons/md";
import { navigationItems } from "#/data/navigation";
import { aboutMe } from "#/data/about";
import { Button } from "./ui/button";

export function Footer() {
  const currentYear = new Date().getFullYear();
  const socialLinks = [
    ...Object.entries(aboutMe.socialLinks).map(([key, social]) => ({
      key,
      href: social.url,
      label: social.label,
      icon: social.icon,
    })),
    {
      key: "email",
      href: `mailto:${aboutMe.email}`,
      label: "Email",
      icon: <MdEmail className="h-full w-full" />,
    },
  ];

  return (
    <footer className="bg-background/80 backdrop-blur-md">
      {/* Top wave decoration */}
      <div className="w-full overflow-hidden">
        <svg
          className="text-background h-12 w-full fill-current dark:text-neutral-950"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            opacity=".25"
          ></path>
          <path
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
            opacity=".5"
          ></path>
          <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
        </svg>
      </div>

      <div className="container mx-auto py-12 md:py-16">
        {/* Main footer content */}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Logo and description - 4 columns on md */}
          <div className="space-y-4 md:col-span-4">
            <Link href="/" className="inline-block text-2xl font-bold">
              <span className="text-primary font-extrabold">Jeff</span>
              <span className="ml-1.5 font-medium">Lefloch</span>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Portfolio professionnel présentant mes compétences, expériences et projets dans le développement web.
            </p>

            {/* Contact info */}
            <div className="space-y-2 pt-4">
              <div className="text-muted-foreground flex items-center text-sm">
                <MapPin className="text-primary mr-2 h-4 w-4" />
                <span>{aboutMe.location}</span>
              </div>
              <div className="text-muted-foreground flex items-center text-sm">
                <MdEmail className="text-primary mr-2 h-4 w-4" />
                <a href={`mailto:${aboutMe.email}`} className="hover:text-primary hover:scale-105">
                  {aboutMe.email}
                </a>
              </div>
              <div className="text-muted-foreground flex items-center text-sm">
                <Calendar className="text-primary mr-2 h-4 w-4" />
                <span>{aboutMe.availability}</span>
              </div>
            </div>
          </div>

          {/* Navigation links - 3 columns on md */}
          <div className="space-y-4 md:col-span-3">
            <h3 className="text-lg font-semibold">Navigation</h3>
            <nav className="flex flex-col space-y-3">
              {navigationItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-muted-foreground hover:text-primary group flex items-center text-sm"
                >
                  <div className="bg-muted group-hover:bg-primary mr-2.5 h-6 w-1 rounded-r-full"></div>
                  {item.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Languages - 2 columns on md */}
          <div className="space-y-4 md:col-span-2">
            <h3 className="text-lg font-semibold">Langues</h3>
            <div className="space-y-3">
              {aboutMe.languages.map((language) => (
                <div key={language.name} className="text-muted-foreground flex items-center text-sm">
                  <span className="mr-2 text-base">{language.emoji}</span>
                  <span>{language.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Social links and CV - 3 columns on md */}
          <div className="space-y-4 md:col-span-3">
            <h3 className="text-lg font-semibold">Réseaux sociaux</h3>
            <div className="flex flex-wrap justify-between gap-2">
              {socialLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-background hover:bg-primary/10 border-border transform rounded-full border p-2.5 transition-transform duration-200 will-change-transform hover:scale-105"
                  aria-label={link.label}
                >
                  <span className="sr-only">{link.label}</span>
                  <span className="block h-5 w-5">{link.icon}</span>
                </a>
              ))}
            </div>

            <Button asChild className="mt-4 w-full">
              <a href="/cv/cv.pdf" target="_blank" rel="noopener noreferrer">
                Télécharger mon CV
              </a>
            </Button>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-muted-foreground mt-12 border-t pt-6 text-center text-sm">
          <p> {currentYear} Jeff Lefloch. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
}
